/* eslint-disable no-fallthrough */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react";
import "./index.scss";
import "./App.scss";
import "@/components/Button/index.scss";
import { useDispatch, useSelector } from "react-redux";
import Header from "./Header";
import BottomTool from "./BottomTool";
import {
  setBook,
  setCurrentPage,
  setCurrentPageItem,
  setDisplayType,
} from "@/redux-store/slices/bookStore";
import {
  setBlankPages,
  setCurrentBlankPage,
} from "@/redux-store/slices/blankPageReducer";

import ToolBox from "./ToolBox";

import {
  DisplayTypes,
  EraserShortCuts,
  PageCanvasModel,
  PageItemCanvasModel,
  PageNavigationDirection,
} from "@/types";
import Calculator from "./ExtraTools/calculator";
import StopWatch from "./ExtraTools/stopwatch";
import MaskingPage from "./ExtraTools/mask";
import { useExtraTool } from "@/contexts/ExtraToolContext";
import { BlankPage } from "@/database/userPageDatabaseService";
import SavedPages from "./SavedPages";
import { useDrawingContext } from "@/contexts/DrawingContext";
import { useDebouncedCallback } from "use-debounce";
import Numerator from "./Numerator";
import SideTools from "./SideTools/SideTools";
import { PuffLoader } from "react-spinners";
import { PageCanvasService } from "@/database/smartBookModelService";
import { AppDispatch, RootState } from "@/redux-store";
import { SmartBookService } from "@/services/smartBookService";
import { getUser } from "@/database/userDatabaseService";
import { setUser } from "@/redux-store/slices/authReducer";
import { get } from "@/data/ionicStorage";
import Sketch from "./Sketch";
import { useParams } from "react-router-dom";
import MobileToolbox from "./ToolBox/MobileToolbox";

const SmartBook = () => {
  const { bookId, pageNo } = useParams();

  // const { t } = useTranslation("translation", { keyPrefix: "books" });
  const store = useSelector((state: RootState) => state.bookReducer);

  const { blankPages, currentBlankPage } = useSelector(
    (state: RootState) => state.blankPageReducer
  );

  const { user } = useSelector((state: RootState) => state.authReducer);

  const {
    book,
    currentPage,
    contentInfo,
    currentPageItem,
    displayType,
    previousDisplayType,
  } = store;
  const [isBottomToolOpen, setIsBottomToolOpen] = useState(true);
  const [isIOS, setIsIOS] = useState(false);
  const {
    isMaskingPageOpen,
    isSavedPageOpen,
    toggleSavedPage,
    isNumeratorOpen,
    toggleNumerator,
    closeNumerator,
  } = useExtraTool();

  const dispatch = useDispatch<AppDispatch>();
  const sketchArea = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<any>(null);

  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Check for mobile screen
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  // iOS device detection - improved
  useEffect(() => {
    const detectIOS = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
      const isMacSafari =
        /macintosh/.test(userAgent) &&
        "ontouchend" in document &&
        /safari/.test(userAgent);
      return isIOSDevice || isMacSafari;
    };

    const isIOSDevice = detectIOS();
    setIsIOS(isIOSDevice);

    // Set CSS variable for iOS safe area inset only if it's iOS
    if (isIOSDevice) {
      // Apply viewport height fix for iOS Safari
      const setViewportHeight = () => {
        // Gerçek viewport yüksekliğini hesapla
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty("--vh", `${vh}px`);

        // Content alanı için yükseklik hesaplama
        const headerHeight = 50; // Default header height
        let bottomToolHeight = 60; // Default bottom tool height

        // Ekran boyutuna göre bottom tool yüksekliğini ayarla
        if (window.innerWidth <= 768) {
          bottomToolHeight = 45; // Mobile
        } else if (window.innerWidth <= 1180) {
          bottomToolHeight = 50; // Tablet
        } else if (window.innerWidth <= 1360) {
          bottomToolHeight = 45; // Small desktop
        }

        // Content alanına özel yükseklik hesaplama
        const contentHeight =
          window.innerHeight - headerHeight - bottomToolHeight;
        document.documentElement.style.setProperty(
          "--content-height",
          `${contentHeight}px`
        );

        // iOS safe area inset dahil etme
        const safeArea =
          window.innerHeight - document.documentElement.clientHeight;
        if (safeArea > 0) {
          document.documentElement.style.setProperty(
            "--ios-content-height",
            `${contentHeight - safeArea}px`
          );
        }
      };

      // Events
      window.addEventListener("resize", setViewportHeight);
      window.addEventListener("orientationchange", setViewportHeight);
      document.addEventListener("visibilitychange", setViewportHeight);
      setViewportHeight();

      // Set safe area insets
      document.documentElement.style.setProperty(
        "--safe-area-inset-bottom",
        "env(safe-area-inset-bottom, 0px)"
      );

      return () => {
        window.removeEventListener("resize", setViewportHeight);
        window.removeEventListener("orientationchange", setViewportHeight);
        document.removeEventListener("visibilitychange", setViewportHeight);
      };
    } else {
      // Android veya diğer mobil cihazlar için de yükseklik hesaplaması
      const setMobileViewportHeight = () => {
        const headerHeight = 50;
        let bottomToolHeight = 60;

        if (window.innerWidth <= 768) {
          bottomToolHeight = 45;
        } else if (window.innerWidth <= 1180) {
          bottomToolHeight = 50;
        } else if (window.innerWidth <= 1360) {
          bottomToolHeight = 45;
        }

        const contentHeight =
          window.innerHeight - headerHeight - bottomToolHeight;
        document.documentElement.style.setProperty(
          "--content-height",
          `${contentHeight}px`
        );
      };

      window.addEventListener("resize", setMobileViewportHeight);
      window.addEventListener("orientationchange", setMobileViewportHeight);
      setMobileViewportHeight();

      return () => {
        window.removeEventListener("resize", setMobileViewportHeight);
        window.removeEventListener(
          "orientationchange",
          setMobileViewportHeight
        );
      };
    }
  }, []);

  // Başlangıç boyutu ayarla (varsayılan değerler)
  const [size, setSize] = useState({ width: 600, height: 800 });

  // ResizeObserver kullanarak boyut değişimlerini takip et
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Yardımcı fonksiyon
    const updateSize = () => {
      if (sketchArea.current) {
        const rect = sketchArea.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setSize({
            width: rect.width,
            height: rect.height,
          });
        }
      }
    };

    // İlk ölçüm
    updateSize();

    // ResizeObserver ile değişimleri izle
    const resizeObserver = new ResizeObserver(() => {
      updateSize();
    });

    if (sketchArea.current) {
      resizeObserver.observe(sketchArea.current);
    }

    // window yeniden boyutlandırma olayını dinle (ek güvence için)
    window.addEventListener("resize", updateSize);

    // Temizleme
    return () => {
      if (sketchArea.current) {
        resizeObserver.unobserve(sketchArea.current);
      }
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, []);

  // useEffect içinde book değişince boyutu güncelle (layout değişimine tepki için)
  useEffect(() => {
    if (sketchArea.current) {
      const rect = sketchArea.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setSize({
          width: rect.width,
          height: rect.height,
        });
      }
    }
  }, [book]);

  const { isNoteAreaActive } = useDrawingContext();
  const {
    currentTool,
    activeSubTool,
    updateActiveSubTool,
    previousTool,
    selectedColor,
    thickness,
    eraseThickness,
    updateSelectedTool,
    updateSelectedColor,
    updateThickness,
    updateEraseThickness,
    pageItemPosition,
    updatePageItemPosition,
  } = useDrawingContext();

  const [isBookLoading, setIsBookLoading] = useState<boolean>();

  const getBookStartPage = async (bookId: number): Promise<number> => {
    let pageNumber = 1;
    if (pageNo) {
      pageNumber = Number(pageNo);
    } else if (user?.userSettings?.continueLastPage) {
      const startPage = await get(`${bookId}-lastPage`);
      if (startPage) {
        pageNumber = Number(startPage);
      }
    }
    return pageNumber;
  };

  const getBook = async (bookId: number) => {
    const smBook = await SmartBookService.getBookById(bookId);
    dispatch(setBook(smBook));
  };

  useEffect(() => {
    setIsBookLoading(true);
    if (bookId) {
      getBook(Number(bookId));
    }
  }, [bookId]);

  const setFirstPage = useDebouncedCallback(async (bookId: number) => {
    const startPage = await getBookStartPage(bookId);
    setPage(startPage);
    setIsBookLoading(false);
  }, 300);

  useEffect(() => {
    if (book && size.width > 0 && size.height > 0) {
      setFirstPage(book.id);
    }
  }, [book, size]);

  const setPage = useCallback(
    async (pageNumber: number): Promise<PageCanvasModel | undefined> => {
      if (book && size.width > 0 && size.height > 0) {
        const page = await PageCanvasService.getBookPageByNumber(
          book,
          pageNumber,
          size.width,
          size.height,
          ""
        );
        if (page) {
          dispatch(setCurrentPage(page));
        }
        return page;
      }
    },
    [book, size, dispatch]
  );

  const getPageNumberForNavigation = (
    direction: PageNavigationDirection
  ): number => {
    const currentPageNo = currentPage?.pageNo || 1;
    const totalPages = book?.pages.length || 1;
    switch (direction) {
      case PageNavigationDirection.Next:
        if (currentPageNo < totalPages) {
          return currentPageNo + 1;
        }
        return currentPageNo;
      case PageNavigationDirection.Previous:
        if (currentPageNo > 1) {
          return (currentPage?.pageNo ?? 0) - 1;
        }
        return currentPageNo;
      default:
        return currentPage?.pageNo || 1;
    }
  };

  const getBlankPageForNavigation = (
    direction: PageNavigationDirection
  ): BlankPage | undefined => {
    const json = JSON.stringify(canvasRef.current.toJSON());
    const currentIndex = blankPages.findIndex(
      (x) =>
        x.id === currentBlankPage.id &&
        x.createdDate === currentBlankPage.createdDate
    );
    const pages = [
      ...blankPages.map((item) => {
        const temp = Object.assign({}, item);
        if (currentBlankPage.id === item.id) {
          temp.json = json;
        }
        return temp;
      }),
    ];
    dispatch(setBlankPages(pages));
    switch (direction) {
      case PageNavigationDirection.Next:
        if (currentIndex < blankPages.length - 1) {
          return blankPages[currentIndex + 1];
        }
        break;
      case PageNavigationDirection.Previous:
        if (currentIndex > 0) {
          return blankPages[currentIndex - 1];
        }
        break;
      default:
        return currentBlankPage;
    }
  };

  const getPageItemForNavigation = async (
    direction: PageNavigationDirection
  ): Promise<PageItemCanvasModel | null> => {
    let pageItem: PageItemCanvasModel | null = null;
    switch (direction) {
      case PageNavigationDirection.Next:
        if (currentPageItem?.nextId) {
          if (size && currentPage) {
            pageItem = await PageCanvasService.getPageItemFromId(
              book?.id || 0,
              size.width,
              size.height,
              currentPage,
              currentPageItem?.nextId
            );
          }
        } else {
          const nextPageNumber = getPageNumberForNavigation(direction);
          const page = await setPage(nextPageNumber);
          if (page && size && page.pageItems.length > 0) {
            pageItem = await PageCanvasService.getPageItemFromId(
              book?.id || 0,
              size.width,
              size.height,
              page,
              page.pageItems[0].id
            );
          } else if (page) {
            dispatch(setDisplayType(DisplayTypes.Page));
          }
        }

        break;
      case PageNavigationDirection.Previous:
        if (currentPageItem?.previousId && size && currentPage) {
          pageItem = await PageCanvasService.getPageItemFromId(
            book?.id || 0,
            size.width,
            size.height,
            currentPage,
            currentPageItem?.previousId
          );
        } else {
          const nextPageNumber = getPageNumberForNavigation(direction);
          const page = await setPage(nextPageNumber);
          if (page && size && page.pageItems.length > 0) {
            pageItem = await PageCanvasService.getPageItemFromId(
              book?.id || 0,
              size.width,
              size.height,
              page,
              page.pageItems[page.pageItems.length - 1].id
            );
          } else if (page) {
            dispatch(setDisplayType(DisplayTypes.Page));
          }
        }
        break;
      default:
        break;
    }
    return pageItem;
  };

  const isPan = (isNext: boolean) => {
    let isPan = false;
    if (false && displayType !== DisplayTypes.BlankPage) {
      isPan = canvasRef.current.absolutePan(isNext);
    }
    return isPan;
  };

  const handlePageChange = useCallback(
    async (direction: PageNavigationDirection) => {
      const isPanApply = isPan(direction === PageNavigationDirection.Next);
      if (isPanApply) {
        return;
      }
      switch (displayType) {
        case DisplayTypes.Page:
          const pageNumber = getPageNumberForNavigation(direction);
          setPage(pageNumber);
          break;
        case DisplayTypes.BlankPage:
          const blankPage = getBlankPageForNavigation(direction);
          if (blankPage) {
            dispatch(setCurrentBlankPage(blankPage));
          }
          break;
        case DisplayTypes.PageItem:
          const pageItem = await getPageItemForNavigation(direction);
          if (pageItem) {
            dispatch(setCurrentPageItem(pageItem));
          }
          break;
        default:
          break;
      }
    },
    [
      isPan,
      displayType,
      getPageNumberForNavigation,
      setPage,
      getBlankPageForNavigation,
      getPageItemForNavigation,
      dispatch,
    ]
  );

  const setAppUser = async () => {
    const appUser = await getUser();
    dispatch(setUser(appUser));
    return appUser;
  };
  useEffect(() => {
    if (!user) {
      setAppUser();
    }
  }, []);

  return (
    <>
      {book && (
        <div className={`smart-book ${isIOS ? "ios-device" : ""}`}>
          <>
            <Header
              bookId={Number(bookId)}
              displayType={displayType}
              contentInfo={contentInfo}
              dispatch={dispatch}
              canvas={canvasRef.current}
              user={user}
            />
            <div
              ref={sketchArea}
              className={`sm-content ${isIOS ? "ios-content" : ""}`}
            >
              {isNoteAreaActive && (
                <div className="note-info">
                  <span>Not Eklemek istediğiniz alana Tıklayınız.</span>
                </div>
              )}
              {currentPage && !isBookLoading && (
                <Sketch
                  bookId={book?.id || 0}
                  userId={user?.id || ""}
                  dispatch={dispatch}
                  setTool={updateSelectedTool}
                  currentPage={currentPage}
                  pageItemPosition={pageItemPosition}
                  currentBlankPage={currentBlankPage}
                  currentPageItem={currentPageItem}
                  setCurrentPageItem={setCurrentPageItem}
                  previousTool={previousTool}
                  currentTool={currentTool}
                  activeSubTool={activeSubTool}
                  handlePageChange={handlePageChange}
                  updateActiveSubTool={updateActiveSubTool}
                  displayType={displayType}
                  width={size?.width ?? 600}
                  height={size?.height ?? 800}
                  ref={canvasRef}
                  selectedColor={selectedColor}
                  eraseThickness={eraseThickness}
                  thickness={thickness}
                  setDisplayType={setDisplayType}
                  eraserShortCut={EraserShortCuts.Closed}
                  hideIcons={false}
                  setCurrentBlankPage={setCurrentBlankPage}
                  blankPages={blankPages}
                />
              )}
              {isBookLoading && (
                <div className="loading-container">
                  <PuffLoader />
                </div>
              )}
              {currentPage && user && (
                <>
                  <ToolBox
                    user={user}
                    dispatch={dispatch}
                    setTool={updateSelectedTool}
                    screenWidth={size?.width || 100}
                    selectedColor={selectedColor}
                    setSelectedColor={updateSelectedColor}
                    thickness={thickness}
                    setThickness={updateThickness}
                    eraseThickness={eraseThickness}
                    setEraserThickness={updateEraseThickness}
                    updateActiveSubTool={updateActiveSubTool}
                    activeSubTool={activeSubTool}
                    displayType={displayType}
                    currentPageItem={currentPageItem}
                    currentTool={currentTool}
                    previousTool={previousTool}
                    pageItemPosition={pageItemPosition}
                    canvas={canvasRef.current}
                  />

                  <SideTools
                    bookId={book?.id || 0}
                    side={"left"}
                    dispatch={dispatch}
                    handlePageChange={handlePageChange}
                    setDisplayType={setDisplayType}
                    setCurrentPageItem={setCurrentPageItem}
                    currentPage={currentPage}
                    displayType={displayType}
                    previousDisplayType={previousDisplayType}
                    getBookPage={function (): void {
                      throw new Error("Function not implemented.");
                    }}
                    absolutePan={function (): boolean {
                      throw new Error("Function not implemented.");
                    }}
                  />
                  <SideTools
                    bookId={book?.id || 0}
                    side={"right"}
                    dispatch={dispatch}
                    handlePageChange={handlePageChange}
                    setDisplayType={setDisplayType}
                    currentPage={currentPage}
                    displayType={displayType}
                    previousDisplayType={previousDisplayType}
                    setCurrentPageItem={setCurrentPageItem}
                    getBookPage={function (): void {
                      throw new Error("Function not implemented.");
                    }}
                    absolutePan={function (): boolean {
                      throw new Error("Function not implemented.");
                    }}
                  />
                </>
              )}
              {isNumeratorOpen && (
                <Numerator setPage={setPage} closeNumerator={closeNumerator} />
              )}
            </div>

            {/* Mobil araç kutusunu doğrudan burada render edelim - Android ve iOS için özel stiller */}
            {isMobile && currentPage && user && (
              <MobileToolbox
                displayType={displayType}
                user={user}
                currentTool={currentTool}
                previousTool={previousTool}
                dispatch={dispatch}
                setTool={updateSelectedTool}
                canvas={canvasRef.current}
                selectedColor={selectedColor}
                setSelectedColor={updateSelectedColor}
                thickness={thickness}
                setThickness={updateThickness}
                eraseThickness={eraseThickness}
                setEraserThickness={updateEraseThickness}
                updateActiveSubTool={updateActiveSubTool}
                activeSubTool={activeSubTool}
                className="mobile-main-toolbox"
                style={{
                  position: "fixed",
                  bottom: isIOS
                    ? "calc(45px + env(safe-area-inset-bottom, 0px))"
                    : "45px",
                  zIndex: 3000,
                }}
              />
            )}

            <div
              className={`sm-bottom-tool ${!isBottomToolOpen ? "close" : ""} ${isIOS ? "ios-bottom-tool" : ""}`}
              style={{ zIndex: isMobile ? 2500 : 3 }}
            >
              {book && currentPage && user && (
                <BottomTool
                  currentPage={currentPage}
                  currentPageItem={currentPageItem}
                  displayType={displayType}
                  toggleSavedPage={toggleSavedPage}
                  pageItemPosition={pageItemPosition}
                  updatePageItemPosition={updatePageItemPosition}
                  sbStore={store}
                  book={book}
                  user={user}
                  setPage={setPage}
                  isBottomToolOpen={isBottomToolOpen}
                  setIsBottomToolOpen={setIsBottomToolOpen}
                  setBlankPages={setBlankPages}
                  isSavedPageOpen={isSavedPageOpen}
                  currentBlankPage={currentBlankPage}
                  blankPages={blankPages}
                  setCurrentBlankPage={setCurrentBlankPage}
                  dispatch={dispatch}
                  handlePageChange={handlePageChange}
                  setDisplayType={setDisplayType}
                  toggleNumerator={toggleNumerator}
                  previousDisplayType={store.previousDisplayType}
                  canvas={canvasRef.current}
                  completeActivity={() => {
                    return canvasRef.current.completeActivity();
                  }}
                  resetActivity={() => canvasRef.current.resetActivity()}
                  showActivityAnswer={() =>
                    canvasRef.current.showActivityAnswer()
                  }
                  showPreviousAnswer={() =>
                    canvasRef.current.showPreviousAnswer()
                  }
                  absolutePan={(e: any) => canvasRef.current.absolutePan(e)}
                  showNextAnswer={() => canvasRef.current.showNextAnswer()}
                  setTool={updateSelectedTool}
                  screenWidth={size?.width || 100}
                  selectedColor={selectedColor}
                  setSelectedColor={updateSelectedColor}
                  thickness={thickness}
                  setThickness={updateThickness}
                  eraseThickness={eraseThickness}
                  setEraserThickness={updateEraseThickness}
                  updateActiveSubTool={updateActiveSubTool}
                  activeSubTool={activeSubTool}
                  currentTool={currentTool}
                  previousTool={previousTool}
                />
              )}
            </div>
          </>
          <Calculator />
          <StopWatch />
          {book && user && (
            <SavedPages
              isOpen={isSavedPageOpen}
              toggleSavedPage={toggleSavedPage}
              dispatch={dispatch}
              setBlankPages={setBlankPages}
              setCurrentBlankPage={setCurrentBlankPage}
              setDisplayType={setDisplayType}
              user={user}
              book={book}
              blankPages={blankPages}
            />
          )}

          {isMaskingPageOpen && <MaskingPage containerRef={sketchArea} />}
        </div>
      )}
    </>
  );
};

export default SmartBook;
